'use client';

import { useEffect, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';
import Link from 'next/link';
import './margin.css';

/* Saari styling margin.css mein hai (scope: .margin-page). Is file mein koi styling nahi.
   Images (public/assets/images/ mein daalo):
   - margin-hero.jpg   -> hero background        (CSS: ".margin-hero-bg-image")
   - margin-cta.jpg    -> final CTA background   (CSS: ".final-cta-bg")
   - margin-retail.jpg, margin-manufacturing.jpg, margin-pharma.jpg,
     margin-auto.jpg, margin-energy.jpg, margin-services.jpg
                         -> industry cards        (neeche `industries` array)

   Links: Industry cards ka link `industries` array ke `href` field me badlo.
   Jis card ka page nahi hai (Auto & Industrial) uska href nahi hai, wo plain card rahega. */

type IconKey =
  | 'chart' | 'target' | 'cube' | 'people' | 'gear' | 'trend'
  | 'tag' | 'pie' | 'file' | 'network' | 'megaphone' | 'download';

function Icon({ name }: { name: IconKey }) {
  const paths: Record<IconKey, ReactNode> = {
    chart: <><path d="M4 20V4M4 20h17M8 16v-4M12 16V8M16 16v-6" /></>,
    target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></>,
    cube: <><path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" /><path d="m4 7.5 8 4.5 8-4.5M12 12v9" /></>,
    people: <><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><path d="M16 5a3 3 0 0 1 0 6M18 14c1.8.8 3 2.6 3 5" /></>,
    gear: <><circle cx="12" cy="12" r="3" /><path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" /></>,
    trend: <><path d="M3 19h18M5 16l5-5 3 3 6-8" /><path d="M14 6h5v5" /></>,
    tag: <><path d="M3 12V4h8l10 10-8 8z" /><circle cx="7.5" cy="8.5" r="1.2" /></>,
    pie: <><path d="M12 3a9 9 0 1 0 9 9h-9z" /><path d="M15 3.5A9 9 0 0 1 20.5 9H15z" /></>,
    file: <><path d="M6 3h8l5 5v13H6z" /><path d="M14 3v5h5M9 13h7M9 17h7" /></>,
    network: <><circle cx="12" cy="5" r="2.5" /><circle cx="5" cy="19" r="2.5" /><circle cx="19" cy="19" r="2.5" /><path d="M12 7.5v4M12 11.5l-6 5.5M12 11.5l6 5.5" /></>,
    megaphone: <><path d="M3 10v4a1 1 0 0 0 1 1h3l8 4V6L7 10H4a1 1 0 0 0-1 0z" /><path d="M18 9a4 4 0 0 1 0 6" /></>,
    download: <><path d="M12 3v12m-5-5 5 5 5-5M4 20h16" /></>,
  };

  return (
    <svg className="mp-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

function Arrow() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M14 5l7 7m0 0l-7 7m7-7H3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
    </svg>
  );
}

/* ============================== DATA ============================== */

const benefits: { icon: IconKey; title: string; text: string }[] = [
  { icon: 'chart', title: 'Higher Profitability', text: 'Improve margins across products, customers and channels' },
  { icon: 'target', title: 'Data-Driven Decisions', text: 'Understand profitability and price for value' },
  { icon: 'cube', title: 'Optimise Product Mix', text: 'Invest more in what works, reduce low-margin offerings' },
  { icon: 'people', title: 'Profitable Customers', text: 'Focus on high-value customers and channels' },
  { icon: 'gear', title: 'Scenario Modelling', text: 'Simulate impact of price, cost, mix and demand changes' },
];

const questions: { icon: IconKey; tone: string; text: string }[] = [
  { icon: 'chart', tone: 'green', text: 'Which products are truly profitable?' },
  { icon: 'people', tone: 'amber', text: 'Which customers and channels create the most value?' },
  { icon: 'tag', tone: 'purple', text: 'Are we pricing correctly?' },
  { icon: 'pie', tone: 'blue', text: 'Where are we losing margin and why?' },
  { icon: 'trend', tone: 'rose', text: 'What is the impact of cost, mix and demand changes?' },
];

/* href = industry page ka link (header ke Industries menu se). Auto & Industrial ka page nahi hai. */
const industries: { title: string; desc: string; image: string; href?: string }[] = [
  { title: 'Retail & CPG', desc: 'Category and SKU profitability, trade spend ROI, promotion impact', image: '/assets/images/Retail-&-CPG1.jpg', href: '/industries/fmcg-retail' },
  { title: 'Manufacturing', desc: 'Product, plant and customer profitability, make vs buy analysis', image: '/assets/images/manufacturing2.jpg', href: '/industries/manufacturing' },
  { title: 'Pharma & Healthcare', desc: 'Brand and molecule profitability, tender pricing, market mix', image: '/assets/images/Pharma-&-Healthcare.jpg', href: '/industries/pharma-lifescience' },
  { title: 'Auto & Industrial', desc: 'Product line and customer profitability, after-sales spare parts margins', image: '/assets/images/Auto-&-Industrial1.jpg' },
  { title: 'Utilities & Energy', desc: 'Tariff and contract profitability, cost to serve, regulatory impact', image: '/assets/images/Utilities-&-Energy.jpg', href: '/industries/energy-utilities' },
  { title: 'Professional Services', desc: 'Project and client profitability, resource margin and service mix', image: '/assets/images/professional-services2.jpg', href: '/industries/professional-services' },
];

const useCases: { icon: IconKey; title: string; desc: string; tone: string }[] = [
  { icon: 'file', title: 'Profitability Analytics', desc: 'Real-time margin analysis by product, customer and channel.', tone: 'green' },
  { icon: 'target', title: 'Price Optimisation', desc: 'AI-driven price recommendations and scenarios.', tone: 'blue' },
  { icon: 'network', title: 'Product Mix Optimisation', desc: 'Identify and grow high-margin products.', tone: 'purple' },
  { icon: 'pie', title: 'Cost-to-Serve Analysis', desc: 'Understand true cost and margin by customer and channel.', tone: 'amber' },
  { icon: 'trend', title: 'Scenario Modelling', desc: 'Simulate impact of price, cost, demand and channel mix.', tone: 'cyan' },
  { icon: 'megaphone', title: 'What-If Analysis', desc: 'Evaluate impact of promotions, new products and market changes.', tone: 'rose' },
];

const problems = [
  'Low product/category margins',
  'Unprofitable customers and channels',
  'Incorrect pricing and discounting',
  'High cost to serve',
  'Poor promotion ROI',
];

const RESOURCES_HREF = '/insights/playbooks-downloads';

const resources: { title: string; type: string; tone: string }[] = [
  { title: 'Profitability Assessment Template', type: 'Excel', tone: 'green' },
  { title: 'Price Optimisation Playbook', type: 'PDF', tone: 'rose' },
  { title: 'Cost-to-Serve Model', type: 'Excel', tone: 'green' },
  { title: 'Customer Profitability Dashboard', type: 'Power BI', tone: 'amber' },
  { title: 'Case Study: CPG Margin Improvement', type: 'PDF', tone: 'rose' },
];

const journey = [
  { title: 'Digital Day', desc: 'Align on business questions and opportunities' },
  { title: 'Discovery & Assessment', desc: 'Analyse current state, data and quick wins' },
  { title: 'Solution Demo', desc: 'Showcase tailored use cases with your data' },
  { title: 'Pilot', desc: 'Prove value in a focused area (4–8 weeks)' },
  { title: 'Scale', desc: 'Enterprise rollout and continuous improvement' },
];

/* ===================== SCROLL ANIMATION ===================== */
/* Sirf fade-up (neeche se upar) aur zoom. Left/right slide nahi. */
const animGroups: [string, 'fade-up' | 'zoom'][] = [
  ['.sec-head', 'fade-up'],
  ['.benefit-grid .eco-card', 'zoom'],
  ['.q-card', 'fade-up'],
  ['.ind-card', 'fade-up'],
  ['.coverage-card', 'zoom'],
  ['.pipeline', 'fade-up'],
  ['.flow-step', 'zoom'],
  ['.final-cta-box', 'zoom'],
];

function useScrollReveal(root: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('rv--in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    animGroups.forEach(([selector, type]) => {
      const perParent = new Map<Element, number>();
      el.querySelectorAll<HTMLElement>(selector).forEach((node) => {
        const parent = node.parentElement as Element;
        const i = perParent.get(parent) ?? 0;
        perParent.set(parent, i + 1);

        node.classList.add('rv', `rv--${type}`);
        node.style.setProperty('--rv-delay', `${(i % 6) * 80}ms`); // stagger
        io.observe(node);
      });
    });

    return () => io.disconnect();
  }, [root]);
}

/* ============================== PAGE ============================== */

export default function Margin() {
  const pageRef = useRef<HTMLDivElement>(null);
  useScrollReveal(pageRef);

  return (
    <div className="margin-page" ref={pageRef}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="margin-hero" data-purpose="hero-section" id="top">
          <div className="margin-hero-bg-image"></div>
          <div className="margin-hero-bg-overlay"></div>
          <div className="margin-hero-grid"></div>
          <div className="margin-hero-glow margin-hero-glow--left"></div>
          <div className="margin-hero-glow margin-hero-glow--right"></div>

          <div className="op-container op-container--left margin-hero-inner">
            <div className="margin-hero-layout">
              <div className="margin-hero-content">
                <h1 className="margin-hero-title">
                  Margin &amp;
                  <span className="margin-hero-title-accent">
                    Profitability
                    <br />
                    Intelligence
                  </span>
                </h1>

                <p className="margin-hero-subtitle">See what drives profit. Fix what doesn’t. Grow what works.</p>

                <p className="margin-hero-text">
                  Use your data, business rules and AI to understand true profitability across products,
                  customers, channels and geographies — and make better, faster decisions.
                </p>

                <div className="margin-hero-actions">
                  <Link className="btn-primary" href="/tellusyourproblem#tell-details">
                    Book a Discovery Session
                    <Arrow />
                  </Link>
                  <Link className="btn-outline" href="#use-cases">
                    See How It Works
                    <span className="play-icon">▶</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================ BENEFITS ============================ */}
        <section className="op-section1 op-section--white" data-purpose="benefits">
          <div className="op-container">
            <div className="benefit-grid">
              {benefits.map((b) => (
                <div className="eco-card" key={b.title}>
                  <div className="eco-icon">
                    <Icon name={b.icon} />
                  </div>
                  <h3 className="eco-title">{b.title}</h3>
                  <p className="eco-desc">{b.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== KEY BUSINESS QUESTIONS ===================== */}
        <section className="op-section op-section--slate" data-purpose="business-questions">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">Real Decisions</div>
              <h2 className="sec-title">Key Business Questions</h2>
              <p className="sec-sub">Turn your margin and profitability questions into intelligent insights.</p>
            </div>

            <div className="q-grid">
              {questions.map((q) => (
                <div className="q-card" key={q.text}>
                  <span className={`q-icon tone-${q.tone}`}>
                    <Icon name={q.icon} />
                  </span>
                  <p className="q-text">{q.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ======================== INDUSTRY SCENARIOS ======================== */}
        <section className="op-section op-section--white" data-purpose="industry-scenarios">
          <div className="op-container">
            <div className="sec-head">
              <div className="sec-badge">Industries</div>
              <h2 className="sec-title">Industry Scenarios</h2>
              <p className="sec-sub">Proven across industries with real-world templates and accelerators.</p>
            </div>

            <div className="ind-grid">
              {industries.map((item) => {
                const body = (
                  <>
                    <div className="ind-image">
                      <img src={item.image} alt="" loading="lazy" />
                    </div>
                    <div className="ind-body">
                      <h3 className="ind-title">{item.title}</h3>
                      <p className="ind-text">{item.desc}</p>
                    </div>
                  </>
                );
                /* href na ho to card plain article rahega (click nahi hoga) */
                return item.href ? (
                  <Link className="ind-card" key={item.title} href={item.href}>
                    {body}
                  </Link>
                ) : (
                  <article className="ind-card" key={item.title}>
                    {body}
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* ======================== AI-ENABLED USE CASES ======================== */}
        <section className="op-section op-section--slate" data-purpose="use-cases" id="use-cases">
          <div className="op-container op-container--left">
            <div className="sec-head">
              <div className="sec-badge">Applied AI</div>
              <h2 className="sec-title">AI-Enabled Use Cases</h2>
              <p className="sec-sub">From insight to action with applied AI.</p>
            </div>

            <div className="coverage-grid">
              {useCases.map((item) => (
                <div className="coverage-card" key={item.title}>
                  <div>
                    <div className={`coverage-icon tone-${item.tone}`}>
                      <Icon name={item.icon} />
                    </div>
                    <h3 className="coverage-title">{item.title}</h3>
                    <p className="coverage-desc">{item.desc}</p>
                  </div>
                  <div className="coverage-foot">
                    <span>Use Case</span>
                    <span className="coverage-dot"></span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= 52 PROBLEMS + RESOURCES ================= */}
        <section className="op-section op-section--white" data-purpose="problems-and-resources" id="resources">
          <div className="op-container">
            <div className="ladder-grid">
              <div className="pipeline">
                <h3 className="pipeline-head">
                  <span>52 Profitability Problems We Solve</span>
                  <span className="pipeline-count">Real problems. Practical solutions.</span>
                </h3>
                {problems.map((p, i) => (
                  <div className="pipeline-row" key={p}>
                    <div className="pipeline-num tone-blue">{String(i + 1).padStart(2, '0')}</div>
                    <div className="pipeline-label">{p}</div>
                  </div>
                ))}
                <div className="pipeline-foot">
                  <Link className="btn-small" href="/52problem">
                    View All 52 Profitability Problems
                    <Arrow />
                  </Link>
                </div>
              </div>

              <div className="pipeline">
                <h3 className="pipeline-head">
                  <span>Resources &amp; Downloads</span>
                  <span className="pipeline-count">Templates, frameworks and case examples.</span>
                </h3>
                {resources.map((r) => (
                  <Link className="pipeline-row" href={RESOURCES_HREF} key={r.title}>
                    <div className={`pipeline-num tone-${r.tone}`}>
                      <Icon name="file" />
                    </div>
                    <div className="pipeline-body">
                      <div className="pipeline-label">{r.title}</div>
                      <p className="pipeline-desc">{r.type}</p>
                    </div>
                    <span className="pipeline-dl">
                      <Icon name="download" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ======================= ENGAGEMENT JOURNEY ======================= */}
        <section className="op-section op-section--slate" data-purpose="engagement-journey">
          <div className="op-container">
            <div className="sec-head sec-head--center">
              <div className="sec-badge">The Journey Roadmap</div>
              <h2 className="sec-title">Our Engagement Journey</h2>
              <p className="sec-sub">From understanding to measurable outcomes.</p>
            </div>

            <div className="flow-scroll">
              <div className="flow-track">
                {journey.map((step, i) => (
                  <div className="flow-step" key={step.title}>
                    <span className="flow-num">{i + 1}</span>
                    <h3 className="flow-title">{step.title}</h3>
                    <p className="flow-desc">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ========================= FINAL CTA ================================ */}
        <section className="final-cta" data-purpose="final-call-to-action" id="contact">
          <div className="final-cta-bg"></div>
          <div className="final-cta-overlay"></div>
          <div className="op-container final-cta-inner">
            <div className="final-cta-box">
              <div className="final-cta-left">
                <h2 className="final-cta-title">Do More. Together.</h2>
                <p className="final-cta-text">
                  Stronger businesses. Let’s unlock the full value in your business.
                </p>
              </div>
              <div className="final-cta-right">
                <Link className="btn-primary btn-primary--cta" href="/tellusyourproblem#tell-details">
                  Talk to Our Team
                  <Arrow />
                </Link>
                <Link className="btn-outline btn-outline--cta" href="#resources">
                  Our Profitability Approach
                  <Arrow />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}